import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Paper,
} from "@mui/material";

const money2 = (v) => {
  const x = Number(String(v ?? "").replace(",", "."));
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

const disabledFieldSx = {
  "& .Mui-disabled": {
    backgroundColor: "#f0f0f0 !important",
    color: "#333 !important",
    WebkitTextFillColor: "#333 !important",
  },
};

const deepFindArray = (obj, key, depth = 6) => {
  if (!obj || typeof obj !== "object" || depth <= 0) return [];
  if (Array.isArray(obj[key])) return obj[key];
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") {
      const r = deepFindArray(v, key, depth - 1);
      if (Array.isArray(r) && r.length) return r;
    }
  }
  return [];
};

const deepFindObject = (obj, key, depth = 6) => {
  if (!obj || typeof obj !== "object" || depth <= 0) return null;
  if (obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key])) return obj[key];
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") {
      const r = deepFindObject(v, key, depth - 1);
      if (r) return r;
    }
  }
  return null;
};

const isProductoRow = (d) => {
  if (!d || typeof d !== "object") return false;
  if (d.producto_id) return true;
  if (d.codigo_principal || d.codigo_auxiliar) return true;
  if (d.producto && typeof d.producto === "object") return true;
  if (typeof d.producto === "string" && d.producto.trim() !== "") return true;
  return false;
};

const normalizeTotalesVenta = (doc) => {
  const t =
    (doc?.totales && typeof doc.totales === "object" ? doc.totales : null) ||
    deepFindObject(doc, "totales") ||
    {};

  return {
    subtotal_15: money2(t.subtotal_15 ?? 0),
    subtotal_5: money2(t.subtotal_5 ?? 0),
    subtotal_0: money2(t.subtotal_0 ?? 0),
    descuento_total: money2(t.descuento_total ?? 0),
    iva_15: money2(t.iva_15 ?? 0),
    iva_5: money2(t.iva_5 ?? 0),
    ice: money2(t.ice ?? 0),
    total: money2(t.total ?? doc?.total ?? 0),
  };
};

const normalizeRowVenta = (d) => {
  const pu = Number(d.precio_unitario ?? d.pvp ?? 0);
  const base = Number(d.base_imponible ?? 0);

  const codigoP = d.codigo_principal ?? "";
  const codigoA = d.codigo_auxiliar ?? "";
  const detalleExtra = String(d.detalle_adicional ?? "").trim();
  const desc = String(d.descripcion ?? "");

  const full =
    `${codigoP}${codigoA ? ` - ${codigoA}` : ""}`.trim() +
    (desc ? ` - ${desc}` : "") +
    (detalleExtra ? ` - ${detalleExtra}` : "");

  return {
    cantidad: String(d.cantidad ?? ""),
    descripcion: full || desc,
    precio_unit: pu,
    iva_pct: d.iva_porcentaje ?? 0,
    desc_pct: d.descuento_pct ?? 0,
    desc_val: d.descuento_valor ?? 0,
    subtotal: base || Number(d.cantidad ?? 0) * pu,
  };
};

const cellSx = { py: 0.6, px: 1, verticalAlign: "middle" };
const headCellSx = { ...cellSx, color: "#fff", whiteSpace: "nowrap", fontWeight: 700 };
const nowrap = { whiteSpace: "nowrap" };

export default function InvoiceProduct({ doc }) {
  const origen = String(doc?.origen || "").toUpperCase();
  const descripcionDoc = String(doc?.descripcion || "");

  const detallesAll = useMemo(() => {
    const a = Array.isArray(doc?.detalles) ? doc.detalles : deepFindArray(doc, "detalles");
    return Array.isArray(a) ? a : [];
  }, [doc]);

  const detallesProductosCompra = useMemo(() => {
    if (origen !== "COMPRA") return [];
    return detallesAll.filter(isProductoRow);
  }, [detallesAll, origen]);

  const rowsCompra = useMemo(() => {
    return detallesProductosCompra.map((d) => {
      const qty = String(d.cantidad ?? "");
      const pu = Number(d.precio_unitario ?? d.valor_unitario ?? d.pvp ?? 0);
      const base =
        Number(d.base_imponible ?? 0) ||
        Number(d.subtotal ?? 0) ||
        Number(d.total_sin_impuestos ?? 0) ||
        Number(d.cantidad ?? 0) * pu;

      return {
        cantidad: qty,
        descripcion: String(d.descripcion ?? ""),
        precio_unit: pu,
        iva_pct: d.iva_porcentaje ?? 0,
        desc_pct: d.descuento_pct ?? 0,
        desc_val: d.descuento_valor ?? 0,
        subtotal: base,
      };
    });
  }, [detallesProductosCompra]);

  const rowsVenta = useMemo(() => detallesAll.map(normalizeRowVenta), [detallesAll]);

  const retencionesVenta = useMemo(() => {
    const a = Array.isArray(doc?.retenciones_detalle)
      ? doc.retenciones_detalle
      : deepFindArray(doc, "retenciones_detalle");
    return Array.isArray(a) ? a : [];
  }, [doc]);

  const resumenGlobalVenta = useMemo(() => {
    const tipoRet = (r) => String(r?.codigo ?? r?.tipo ?? "").trim().toUpperCase();
    const codRet = (r) => String(r?.codigo_retencion ?? "").trim();

    const ir = retencionesVenta.find((r) => {
      const t = tipoRet(r);
      return t === "1" || t === "IR";
    });

    const iva = retencionesVenta.find((r) => {
      const t = tipoRet(r);
      return t === "2" || t === "IVA";
    });

    return { ir: ir ? codRet(ir) : "", iva: iva ? codRet(iva) : "" };
  }, [retencionesVenta]);

  const totVenta = useMemo(() => normalizeTotalesVenta(doc), [doc]);

  const rows = origen === "COMPRA" ? rowsCompra : rowsVenta;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ tableLayout: "auto" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={headCellSx}>Cant.</TableCell>
              <TableCell sx={headCellSx}>Descripción</TableCell>
              <TableCell sx={headCellSx} align="right">Precio U.</TableCell>
              <TableCell sx={headCellSx} align="right">IVA (%)</TableCell>
              <TableCell sx={headCellSx} align="right">Desc. (%)</TableCell>
              <TableCell sx={headCellSx} align="right">Desc. ($)</TableCell>
              {origen !== "COMPRA" && <TableCell sx={headCellSx}>Ret. IR</TableCell>}
              {origen !== "COMPRA" && <TableCell sx={headCellSx}>Ret. IVA</TableCell>}
              <TableCell sx={headCellSx} align="right">Subtotal</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={origen === "COMPRA" ? 7 : 9} align="center">
                  No hay productos registrados.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={cellSx}>
                    <TextField
                      value={String(r.cantidad ?? "")}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 60, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, minWidth: 300 }}>
                    <Typography sx={{ color: "#111827" }}>{String(r.descripcion)}</Typography>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(r.precio_unit)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={String(r.iva_pct ?? 0)}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 70, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={money2(r.desc_pct)}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 70, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(r.desc_val)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 70, ...disabledFieldSx }}
                    />
                  </TableCell>

                  {origen !== "COMPRA" && (
                    <TableCell sx={cellSx}>
                      <TextField
                        value={resumenGlobalVenta.ir || "---"}
                        disabled
                        size="small"
                        inputProps={{ readOnly: true }}
                        sx={{ width: 80, ...disabledFieldSx }}
                      />
                    </TableCell>
                  )}

                  {origen !== "COMPRA" && (
                    <TableCell sx={cellSx}>
                      <TextField
                        value={resumenGlobalVenta.iva || "---"}
                        disabled
                        size="small"
                        inputProps={{ readOnly: true }}
                        sx={{ width: 80, ...disabledFieldSx }}
                      />
                    </TableCell>
                  )}

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(r.subtotal)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8}>
          <Typography sx={{ mb: 1 }}>Descripción:</Typography>
          <TextField
            value={descripcionDoc}
            multiline
            minRows={3}
            fullWidth
            disabled
            inputProps={{ readOnly: true }}
            sx={disabledFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Grid container spacing={1}>
              {origen === "COMPRA" ? (
                <>
                  {[
                    ["Subtotal 15%", money2(doc?.totales?.subtotal_15 ?? doc?.subtotal_15 ?? 0)],
                    ["Subtotal 5%", money2(doc?.totales?.subtotal_5 ?? doc?.subtotal_5 ?? 0)],
                    ["Subtotal 0%", money2(doc?.totales?.subtotal_0 ?? doc?.subtotal_0 ?? 0)],
                    ["Descuento", money2(doc?.totales?.descuento_total ?? doc?.totales?.descuento ?? doc?.descuento_total ?? 0)],
                    ["IVA 15%", money2(doc?.totales?.iva_15 ?? doc?.iva_15 ?? 0)],
                    ["IVA 5%", money2(doc?.totales?.iva_5 ?? doc?.iva_5 ?? 0)],
                    ["ICE", money2(doc?.totales?.ice ?? doc?.ice ?? 0)],
                    ["Total", money2(doc?.totales?.total ?? doc?.total ?? 0)],
                  ].map(([label, value], index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={7}>
                        <Typography sx={{ color: "#333", fontWeight: label === "Total" ? "bold" : "normal" }}>
                          {label}:
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField value={`$${value}`} fullWidth size="small" disabled sx={disabledFieldSx} />
                      </Grid>
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <>
                  {[
                    ["Subtotal 15%", totVenta.subtotal_15],
                    ["Subtotal 5%", totVenta.subtotal_5],
                    ["Subtotal 0%", totVenta.subtotal_0],
                    ["Descuento", totVenta.descuento_total],
                    ["IVA 15%", totVenta.iva_15],
                    ["IVA 5%", totVenta.iva_5],
                    ["ICE", totVenta.ice],
                    ["Total", totVenta.total],
                  ].map(([label, value], index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={7}>
                        <Typography sx={{ color: "#333", fontWeight: label === "Total" ? "bold" : "normal" }}>
                          {label}:
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField value={`$${money2(value)}`} fullWidth size="small" disabled sx={disabledFieldSx} />
                      </Grid>
                    </React.Fragment>
                  ))}
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
